import { Link } from '@tanstack/react-router';
import { ChevronRight, Search, SearchX } from 'lucide-react';
import { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { EmptyState } from '@nucleus-commerce/admin-kit/layout';
import { usePermissions } from '@nucleus-commerce/admin-kit/permissions';
import { Input } from '@nucleus-commerce/ui/components/input';

import { SETTINGS_TILE_GROUPS, type Tile, type TileGroup } from '../schemas/settings-tiles';

const HUB_GROUP_TOUR: Record<TileGroup['key'], string> = {
  selling: 'settings.hub.group.selling',
  orders: 'settings.hub.group.orders',
  onlineStore: 'settings.hub.group.online-store',
  developers: 'settings.hub.group.developers',
};

function SettingsTile({ tile, storeId }: { tile: Tile; storeId: string }) {
  const { t } = useTranslation('stores');
  const id = useId();
  const Icon = tile.icon;
  return (
    <Link
      to={tile.to}
      params={{ storeId }}
      aria-labelledby={`${id}-title`}
      aria-describedby={`${id}-description`}
      className="group bg-card text-card-foreground hover:bg-accent/50 hover:border-foreground/20 focus-visible:ring-ring focus-visible:ring-offset-background flex h-full items-start gap-3 rounded-lg border p-4 shadow-xs transition-colors outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
    >
      <span className="bg-muted text-muted-foreground group-hover:text-foreground flex size-9 shrink-0 items-center justify-center rounded-md transition-colors">
        <Icon className="size-4" aria-hidden />
      </span>
      <span className="min-w-0 flex-1 space-y-1">
        <span id={`${id}-title`} className="block text-sm font-medium">
          {t(`hub.tiles.${tile.key}.title`)}
        </span>
        <span id={`${id}-description`} className="text-muted-foreground block text-sm">
          {t(`hub.tiles.${tile.key}.description`)}
        </span>
      </span>
      <ChevronRight
        className="text-muted-foreground mt-0.5 size-4 shrink-0 transition-transform group-hover:translate-x-0.5"
        aria-hidden
      />
    </Link>
  );
}

/** The store's settings screens as a searchable grid of tiles, grouped by what they configure. */
export function StoreSettingsHub({ storeId }: { storeId: string }) {
  const { t } = useTranslation('stores');
  const { can } = usePermissions();
  const [query, setQuery] = useState('');
  const needle = query.trim().toLocaleLowerCase();

  const groups = SETTINGS_TILE_GROUPS.map((group) => ({
    key: group.key,
    tiles: group.tiles.filter((tile) => {
      if (!can(tile.permission, tile.mode)) return false;
      if (!needle) return true;
      const text = `${t(`hub.tiles.${tile.key}.title`)} ${t(`hub.tiles.${tile.key}.description`)}`;
      return text.toLocaleLowerCase().includes(needle);
    }),
  })).filter((group) => group.tiles.length > 0);

  return (
    <div className="flex flex-col gap-8">
      <div className="relative max-w-sm" data-tour="settings.hub.search">
        <Search
          className="text-muted-foreground pointer-events-none absolute top-1/2 left-2.5 size-4 -translate-y-1/2"
          aria-hidden
        />
        <Input
          type="search"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          aria-label={t('hub.search')}
          placeholder={t('hub.searchPlaceholder')}
          className="pl-8"
        />
      </div>
      {groups.length === 0 ? (
        needle ? (
          <EmptyState
            icon={SearchX}
            title={t('hub.empty.title', { query: query.trim() })}
            description={t('hub.empty.description')}
          />
        ) : (
          <EmptyState title={t('hub.none.title')} description={t('hub.none.description')} />
        )
      ) : (
        groups.map((group) => (
          <section
            key={group.key}
            aria-labelledby={`hub-${group.key}`}
            className="space-y-3"
            data-tour={HUB_GROUP_TOUR[group.key]}
          >
            <h2 id={`hub-${group.key}`} className="text-muted-foreground text-sm font-medium">
              {t(`hub.groups.${group.key}`)}
            </h2>
            <ul className="grid grid-cols-1 gap-3 min-[1100px]:grid-cols-3 sm:grid-cols-2">
              {group.tiles.map((tile) => (
                <li key={tile.key} className="min-w-0">
                  <SettingsTile tile={tile} storeId={storeId} />
                </li>
              ))}
            </ul>
          </section>
        ))
      )}
    </div>
  );
}
